/**
 * TeamInvitesPanel — owner/admin flow: invite an email into the org.
 * Lists pending invites + an inline form. Shows the accept URL so the
 * owner can paste it into whichever channel (Slack/email/etc.).
 */
import React, { useEffect, useState } from "react";
import { UserPlus, Mail, Copy, X, Loader2, Send, ShieldCheck, Users } from "lucide-react";
import client from "../../../lib/apiClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamInvitesPanel() {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Member");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lastInviteUrl, setLastInviteUrl] = useState("");
  const [operatorAccess, setOperatorAccess] = useState(false);
  const [accessBusy, setAccessBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, acc] = await Promise.all([
        client.get("/orgs/invites"),
        client.get("/orgs/csuite-access").catch(() => ({ data: { share_csuite_with_operators: false } })),
      ]);
      setInvites(inv.data.invites || []);
      setOperatorAccess(!!acc.data.share_csuite_with_operators);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load invites");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true); setLastInviteUrl("");
    try {
      const { data } = await client.post("/orgs/invite", { email: email.trim().toLowerCase(), role });
      setLastInviteUrl(`${BACKEND_URL}${data.invite_url}`);
      setEmail("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite?")) return;
    try { await client.delete(`/orgs/invites/${id}`); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Revoke failed"); }
  };

  const copy = (t) => { try { navigator.clipboard.writeText(t); } catch { /* ignore */ } };

  const toggleOperatorAccess = async () => {
    setAccessBusy(true);
    try {
      const next = !operatorAccess;
      await client.post("/orgs/csuite-access", { enabled: next });
      setOperatorAccess(next);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Toggle failed");
    } finally { setAccessBusy(false); }
  };

  return (
    <div className="space-y-5">
      {/* C-Suite Access Toggle */}
      <div className="p-5 rounded-md border" style={{ background: "#FFFFFF", borderColor: "#F0D3D8" }}
           data-testid="csuite-access-panel">
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-sm grid place-items-center text-white shrink-0"
                style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)" }}>
            <Users size={14}/>
          </span>
          <div className="flex-1">
            <div className="font-display text-base font-extrabold text-[#0F1723]">C-Suite Access · Operators</div>
            <div className="text-[12px] text-zinc-700 mt-1 leading-relaxed max-w-2xl">
              By default only <b>CIO / CFO</b> roles can open the C-Suite portal. Enable this to let
              <b> Operators</b> (project managers, delivery leads) also see the rollup, cockpits, NAVBOT and Board Pack.
              Owners and Admins always have access.
            </div>
            <button
              type="button"
              onClick={toggleOperatorAccess}
              disabled={accessBusy}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest font-semibold border transition-colors"
              style={
                operatorAccess
                  ? { background: "#DCFCE7", color: "#166534", borderColor: "#86EFAC" }
                  : { background: "#FAFAF9", color: "#52525B", borderColor: "#D6D3D1" }
              }
              data-testid="btn-toggle-operator-csuite"
            >
              {accessBusy ? <Loader2 size={11} className="animate-spin"/> : (operatorAccess ? "✓" : "○")}
              {operatorAccess ? "Operators can access C-Suite" : "Operators restricted (boardroom-only)"}
            </button>
          </div>
        </div>
      </div>

      <div className="p-5 rounded-md border" style={{ background: "#FFFFFF", borderColor: "#F0D3D8" }}
         data-testid="team-invites-panel">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-sm grid place-items-center text-white"
              style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}>
          <UserPlus size={14}/>
        </span>
        <div>
          <div className="font-display text-base font-extrabold text-[#0F1723]">Invite teammates</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Owner or Admin only · single-use links · 7-day expiry</div>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 mb-4">
        <div className="flex-1 min-w-[240px]">
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Teammate email</label>
          <div className="relative mt-1">
            <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                   className="fld w-full pl-8" placeholder="colleague@yourcompany.com"
                   data-testid="invite-email-input"/>
          </div>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Role</label>
          <select value={role} onChange={e => setRole(e.target.value)} className="fld mt-1" data-testid="invite-role-select">
            <option value="Member">Member</option>
            <option value="Admin">Admin</option>
            <option value="Owner">Owner</option>
          </select>
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy} data-testid="btn-send-invite">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>} Send invite
        </button>
      </form>

      {err && <div className="p-2.5 rounded-sm bg-red-50 border border-red-200 text-red-800 text-sm mb-3" data-testid="invite-error">{err}</div>}

      {lastInviteUrl && (
        <div className="p-3 rounded-sm mb-4 flex items-center gap-2 text-[12px]"
             style={{ background: "#F0FDF4", border: "1px solid #86EFAC" }}
             data-testid="invite-link-callout">
          <ShieldCheck size={14} className="text-emerald-700 shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-emerald-800">Invite created — send this link</div>
            <div className="font-mono text-[11px] text-emerald-900 truncate">{lastInviteUrl}</div>
          </div>
          <button className="btn btn-ghost btn-xs" onClick={() => copy(lastInviteUrl)} title="Copy" data-testid="btn-copy-invite">
            <Copy size={11}/> Copy
          </button>
        </div>
      )}

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
          Pending invites ({invites.length})
        </div>
        {loading ? (
          <div className="p-3 text-center"><Loader2 size={14} className="inline animate-spin text-zinc-400"/></div>
        ) : invites.length === 0 ? (
          <div className="p-3 text-[12px] italic text-zinc-400">No pending invites.</div>
        ) : (
          <div className="space-y-1.5">
            {invites.map((i) => (
              <div key={i.id} className="flex items-center gap-2 p-2 rounded-sm border text-[12px]"
                   style={{ background: "#FDFBFB", borderColor: "#F0D3D8" }}
                   data-testid={`invite-row-${i.email}`}>
                <span className="w-6 h-6 rounded-sm grid place-items-center shrink-0"
                      style={{ background: "#FDF2F4", color: "#7A1F2B" }}>
                  <Mail size={11}/>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-[#0F1723] truncate">{i.email}</span>
                  <span className="block font-mono text-[10px] text-zinc-500">
                    {i.role} · invited by {i.invited_by} · expires {String(i.expires_at || "").slice(0, 10)}
                  </span>
                </span>
                <button className="btn btn-ghost btn-xs text-red-600" onClick={() => revoke(i.id)} data-testid={`btn-revoke-${i.email}`}>
                  <X size={11}/> Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
